<template>
  <form
    class="v3-form behaviour-create-positive-behaviour-form"
    :data-testid="
      isEditMode
        ? 'behaviour-edit-positive-behaviour-form'
        : 'behaviour-create-positive-behaviour-form'
    "
    @submit.prevent="onSubmit"
  >
    <div
      class="row mb-3 g-3 behaviour-create-positive-behaviour-form__name-core-row"
    >
      <div
        class="col-12 col-md-6 behaviour-create-positive-behaviour-form__field"
      >
        <fieldset class="behaviour-core-value-status-field border-0 p-0 m-0">
          <legend
            id="behaviour-pb-status-legend"
            class="v3-form-label required d-block mb-3"
          >
            {{ fieldLabels.status }}
          </legend>
          <p
            v-if="state.isLoadingCoreValues"
            class="text-muted small mb-3"
            aria-live="polite"
          >
            {{
              $t("behaviour_settings.positive_behaviour_core_values_loading", {
                coreValuesLower: behaviourTerminology.coreValuesLower
              })
            }}
          </p>
          <div v-else-if="state.coreValuesLoadError" class="mb-3" role="alert">
            <InfoBox class="mb-0" danger>
              {{ state.coreValuesLoadError }}
            </InfoBox>
          </div>
          <div
            class="behaviour-core-value-status-radios d-flex flex-wrap gap-4"
            role="radiogroup"
            aria-labelledby="behaviour-pb-status-legend"
          >
            <div
              v-for="option in statusOptions"
              :key="option.value"
              class="behaviour-core-value-status-radio d-flex align-items-center gap-2"
            >
              <input
                :id="`behaviour-pb-status-${option.value}`"
                type="radio"
                name="behaviour-pb-status"
                class="behaviour-core-value-status-radio__input"
                :value="option.value"
                :checked="Number(state.form.status) === Number(option.value)"
                :data-testid="`behaviour-pb-status-${option.value}`"
                @change="onStatusChange($event.target.value)"
              />
              <label
                :for="`behaviour-pb-status-${option.value}`"
                class="behaviour-core-value-status-radio__label mb-0"
              >
                {{ option.label }}
              </label>
            </div>
          </div>
        </fieldset>
      </div>

      <div
        class="col-12 col-md-6 behaviour-create-positive-behaviour-form__field"
      >
        <Label
          required
          label-id="behaviour-pb-core-value-lbl"
          html-for="behaviour-pb-core-value-input"
        >
          {{ fieldLabels.coreValue }}
        </Label>
        <BehaviourCoreValueLazySelect
          :model-value="state.form.core_value_id"
          class="w-100"
          hide-floating-label
          labelled-by="behaviour-pb-core-value-lbl"
          status-filter="active"
          :placeholder="fieldLabels.coreValuePlaceholder"
          :selected-option="state.coreValueSelectedOption"
          :invalid-feedback="errors.core_value_id"
          input-id="behaviour-pb-core-value-input"
          append-dropdown-to-body
          append-dropdown-fixed-position
          data-testid="behaviour-pb-core-value-select"
          @update:model-value="(v) => patchForm('core_value_id', v)"
          @option-selected="onCoreValueSelected"
        />
      </div>
    </div>

    <div
      class="row mb-3 g-3 behaviour-create-positive-behaviour-form__name-core-row"
    >
      <div
        class="col-12 col-md-6 behaviour-create-positive-behaviour-form__field"
      >
        <Label
          required
          label-id="behaviour-pb-name-lbl"
          html-for="behaviour-pb-name"
        >
          {{ fieldLabels.name }}
        </Label>
        <InputField
          id="behaviour-pb-name"
          label-id="behaviour-pb-name-lbl"
          class="w-100"
          :model-value="state.form.name"
          :invalid-feedback="errors.name"
          :max-length="String(maxNameLength)"
          data-testid="behaviour-pb-name-input"
          @update:model-value="(v) => patchForm('name', v)"
        />
      </div>

      <div
        class="col-12 col-md-6 behaviour-create-positive-behaviour-form__points-field"
      >
        <Label
          required
          label-id="behaviour-pb-points-lbl"
          html-for="behaviour-pb-points"
        >
          {{ fieldLabels.points }}
        </Label>
        <InputField
          id="behaviour-pb-points"
          label-id="behaviour-pb-points-lbl"
          class="w-100"
          input-type="number"
          :model-value="state.form.points_awarded"
          :invalid-feedback="errors.points_awarded"
          :min="String(pointsMin)"
          :max="String(pointsMax)"
          data-testid="behaviour-pb-points-input"
          @update:model-value="(v) => patchForm('points_awarded', v)"
        />
      </div>
    </div>

    <div class="mb-3">
      <Label
        required
        label-id="behaviour-pb-award-lbl"
        html-for="behaviour-pb-award"
      >
        {{ fieldLabels.awardDescription }}
      </Label>
      <InputField
        id="behaviour-pb-award"
        label-id="behaviour-pb-award-lbl"
        class="w-100"
        type="textarea"
        :rows="4"
        :model-value="state.form.award_description"
        :invalid-feedback="errors.award_description"
        :max-length="String(maxAwardDescriptionLength)"
        data-testid="behaviour-pb-award-description-input"
        @update:model-value="(v) => patchForm('award_description', v)"
      />
    </div>

    <InfoBox
      v-if="state.serverMessage"
      class="mb-3"
      :class="{ danger: state.isServerError }"
      :title="state.serverTitle"
      role="status"
      aria-live="polite"
    >
      {{ state.serverMessage }}
    </InfoBox>

    <div class="text-center">
      <LoadingButton
        class="me-2 px-4"
        type="button"
        rounded
        data-testid="behaviour-pb-cancel"
        @click="onCancel"
      >
        {{ $t("common.cancel") }}
      </LoadingButton>
      <LoadingButton
        class="px-4"
        type="submit"
        solid
        rounded
        :is-loading="state.isProcessing"
        :disabled="state.isProcessing || state.isLoadingCoreValues"
        :data-testid="
          isEditMode ? 'behaviour-pb-update' : 'behaviour-pb-submit'
        "
      >
        {{ submitButtonLabel }}
      </LoadingButton>
    </div>
  </form>
</template>

<script>
import useAppToast from "@/composables/useAppToast.js"
import {
  createEmptyPositiveBehaviourForm,
  parsePositiveBehaviourApiError,
  positiveBehaviourRowToForm,
  validatePositiveBehaviourForm
} from "@/helpers/behaviourPositiveBehaviourForm.js"
import behaviourPositiveBehaviourConstants from "@/constants/behaviourPositiveBehaviourConstants.js"
import BehaviourCoreValueLazySelect from "@/v3components/Behaviour/BehaviourCoreValueLazySelect.vue"
import InfoBox from "@/v3components/shared/Alerts/InfoBox.vue"
import LoadingButton from "@/v3components/shared/Buttons/LoadingButton.vue"
import InputField from "@/v3components/shared/Form/InputField.vue"
import Label from "@/v3components/shared/Form/Label.vue"
import { computed, inject, onMounted, reactive } from "vue"
import { useI18n } from "vue-i18n"
import { useStore } from "vuex"

export default {
  name: "BehaviourCreatePositiveBehaviourForm",
  components: {
    BehaviourCoreValueLazySelect,
    InfoBox,
    InputField,
    Label,
    LoadingButton
  },
  props: {
    existingNames: {
      type: Array,
      default: () => []
    },
    editablePositiveBehaviour: {
      type: Object,
      default: null
    }
  },
  emits: ["cancel"],
  setup(props, { emit }) {
    const { t } = useI18n()
    const store = useStore()
    const modal = inject("modal")
    const toast = useAppToast()

    const maxNameLength = behaviourPositiveBehaviourConstants.MAX_NAME_LENGTH
    const maxAwardDescriptionLength =
      behaviourPositiveBehaviourConstants.MAX_AWARD_DESCRIPTION_LENGTH
    const pointsMin = behaviourPositiveBehaviourConstants.POINTS_MIN
    const pointsMax = behaviourPositiveBehaviourConstants.POINTS_MAX

    const behaviourTerminology = computed(
      () => store.getters["behaviourSettings/behaviourTerminology"]
    )

    const isEditMode = computed(() =>
      Boolean(props.editablePositiveBehaviour?.id)
    )

    const submitButtonLabel = computed(() =>
      isEditMode.value
        ? t("behaviour_settings.update_positive_behaviour_submit")
        : t("behaviour_settings.create_positive_behaviour_submit")
    )

    const fieldLabels = computed(() => {
      const bt = behaviourTerminology.value
      return {
        name: t("behaviour_settings.positive_behaviour_name_label", {
          positiveBehaviours: bt.positiveBehaviours
        }),
        coreValue: bt.coreValue,
        coreValuePlaceholder: t(
          "behaviour_settings.positive_behaviour_core_value_placeholder",
          { coreValue: bt.coreValue }
        ),
        awardDescription: t(
          "behaviour_settings.positive_behaviour_award_description_label"
        ),
        points: t("behaviour_settings.positive_behaviour_points_label", {
          points: bt.points
        }),
        status: t("behaviour_settings.positive_behaviour_status_label")
      }
    })

    const statusOptions = computed(() => [
      {
        value: 1,
        label: behaviourPositiveBehaviourConstants.STATUS.ENABLED
      },
      {
        value: 0,
        label: behaviourPositiveBehaviourConstants.STATUS.DISABLED
      }
    ])

    const state = reactive({
      form: createEmptyPositiveBehaviourForm(),
      coreValueSelectedOption: null,
      isProcessing: false,
      serverTitle: "",
      serverMessage: "",
      isServerError: false,
      touched: false,
      apiFieldErrors: {}
    })

    const validationErrors = computed(() => {
      if (!state.touched) return {}
      const clientErrors = validatePositiveBehaviourForm(
        state.form,
        isEditMode.value ? props.existingNames : [],
        {
          excludeName: props.editablePositiveBehaviour?.name
        }
      )
      return { ...clientErrors, ...state.apiFieldErrors }
    })

    const fieldErrorLabels = computed(() => ({
      name: fieldLabels.value.name,
      core_value_id: fieldLabels.value.coreValue,
      award_description: fieldLabels.value.awardDescription,
      points_awarded: fieldLabels.value.points,
      status: fieldLabels.value.status
    }))

    const errorMessages = computed(() => {
      const bt = behaviourTerminology.value
      return {
        maxLength: t("behaviour_settings.positive_behaviour_error_max_length", {
          max: maxNameLength
        }),
        duplicate: t("behaviour_settings.positive_behaviour_error_duplicate", {
          positiveBehaviours: bt.positiveBehaviours
        }),
        pointsRange: t(
          "behaviour_settings.positive_behaviour_error_points_range",
          {
            min: pointsMin,
            max: pointsMax
          }
        )
      }
    })

    const errors = computed(() => {
      const raw = validationErrors.value
      const messages = errorMessages.value
      const labels = fieldErrorLabels.value
      const out = {}
      Object.keys(raw).forEach((key) => {
        const code = raw[key]
        if (key === "award_description" && code === "maxLength") {
          out[key] = t(
            "behaviour_settings.positive_behaviour_error_max_length",
            { max: maxAwardDescriptionLength }
          )
        } else if (key === "name" && code === "maxLength") {
          out[key] = t(
            "behaviour_settings.positive_behaviour_error_max_length",
            { max: maxNameLength }
          )
        } else if (code === "required") {
          out[key] = t(
            "behaviour_settings.positive_behaviour_error_required_field",
            { field: labels[key] || key }
          )
        } else if (code === "duplicate") {
          out[key] = messages.duplicate
        } else if (code === "maxLength") {
          out[key] = messages.maxLength
        } else if (code === "range") {
          out[key] = messages.pointsRange
        } else {
          out[key] = t(
            "behaviour_settings.positive_behaviour_error_required_field",
            { field: labels[key] || key }
          )
        }
      })
      return out
    })

    const patchForm = (key, value) => {
      if (!key) return
      state.form[key] = value ?? ""
      state.serverMessage = ""
      state.isServerError = false
      if (state.apiFieldErrors[key]) {
        const next = { ...state.apiFieldErrors }
        delete next[key]
        state.apiFieldErrors = next
      }
    }

    const onStatusChange = (value) => {
      const parsed = Number(value)
      patchForm("status", Number.isNaN(parsed) ? value : parsed)
    }

    const onCoreValueSelected = (option) => {
      if (!option) return
      state.coreValueSelectedOption = { ...option }
    }

    onMounted(() => {
      if (props.editablePositiveBehaviour) {
        Object.assign(
          state.form,
          positiveBehaviourRowToForm(props.editablePositiveBehaviour)
        )
        const editable = props.editablePositiveBehaviour
        const editableId = editable?.coreValueId ?? editable?.core_value_id
        const rawName =
          editable?.coreValueName ?? editable?.core_value_name ?? ""
        const editableName = rawName == null ? "" : String(rawName).trim()
        if (editableId != null && editableId !== "") {
          state.coreValueSelectedOption = {
            label: editableName || String(editableId),
            value: editableId
          }
        }
      }
    })

    const onCancel = () => {
      emit("cancel")
    }

    const onSubmit = async () => {
      state.touched = true
      state.apiFieldErrors = {}
      const fieldErrors = validatePositiveBehaviourForm(
        state.form,
        isEditMode.value ? props.existingNames : [],
        {
          excludeName: props.editablePositiveBehaviour?.name
        }
      )
      if (Object.keys(fieldErrors).length) return

      try {
        state.isProcessing = true
        state.isServerError = false
        state.serverTitle = ""

        const selectedCoreValue =
          state.coreValueSelectedOption &&
          String(state.coreValueSelectedOption.value) ===
            String(state.form.core_value_id)
            ? state.coreValueSelectedOption
            : null

        const positiveBehaviourTerm =
          behaviourTerminology.value.positiveBehaviours

        if (isEditMode.value) {
          await store.dispatch("behaviourSettings/updatePositiveBehaviour", {
            id: props.editablePositiveBehaviour.id,
            form: state.form,
            existingRow: props.editablePositiveBehaviour
          })
          state.serverMessage = t(
            "behaviour_settings.positive_behaviour_updated_success",
            { positiveBehaviours: positiveBehaviourTerm }
          )
          modal?.setAction?.("positiveBehaviourUpdated", {
            id: props.editablePositiveBehaviour.id,
            form: { ...state.form },
            coreValue: selectedCoreValue
              ? { id: selectedCoreValue.value, name: selectedCoreValue.label }
              : null
          })
        } else {
          const { id } = await store.dispatch(
            "behaviourSettings/createPositiveBehaviour",
            state.form
          )
          state.serverMessage = t(
            "behaviour_settings.positive_behaviour_created_success",
            { positiveBehaviours: positiveBehaviourTerm }
          )
          modal?.setAction?.("positiveBehaviourCreated", {
            ...state.form,
            id,
            coreValue: selectedCoreValue
              ? { id: selectedCoreValue.value, name: selectedCoreValue.label }
              : null
          })
        }

        setTimeout(() => {
          emit("cancel")
        }, 600)
      } catch (error) {
        const parsed = parsePositiveBehaviourApiError(error)
        if (parsed.field === "name" && parsed.code === "duplicate") {
          state.apiFieldErrors = { name: "duplicate" }
          state.isServerError = false
          state.serverTitle = ""
          state.serverMessage = ""
          return
        }

        const positiveBehaviourTerm =
          behaviourTerminology.value.positiveBehaviours
        const fallbackMessage = isEditMode.value
          ? t("behaviour_settings.positive_behaviour_updated_error", {
              positiveBehaviours: positiveBehaviourTerm
            })
          : t("behaviour_settings.positive_behaviour_created_error", {
              positiveBehaviours: positiveBehaviourTerm
            })
        const errorMessage = parsed.message || fallbackMessage

        if (isEditMode.value) {
          toast.error(errorMessage)
          return
        }

        state.isServerError = true
        state.serverTitle = t(
          "behaviour_settings.positive_behaviour_error_title"
        )
        state.serverMessage = errorMessage
      } finally {
        state.isProcessing = false
      }
    }

    return {
      behaviourTerminology,
      state,
      errors,
      isEditMode,
      submitButtonLabel,
      fieldLabels,
      statusOptions,
      maxNameLength,
      maxAwardDescriptionLength,
      pointsMin,
      pointsMax,
      patchForm,
      onStatusChange,
      onCoreValueSelected,
      onCancel,
      onSubmit
    }
  }
}
</script>
